import React, { useState, useEffect, useRef } from 'react';
import { Edit3 } from 'lucide-react';
import ReviewStats from './ReviewStats';
import ReviewList from './ReviewList';
import ReviewForm from './ReviewForm';
import { getProductReviews, getProductReviewStats } from '../services/reviewService';

const CommunityReviewSection = ({ productId, productCategory }) => {
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [filters, setFilters] = useState({});
  const [showStickyButton, setShowStickyButton] = useState(false);
  
  const formRef = useRef(null);
  const sectionRef = useRef(null);

  const fetchReviewData = async (options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const sortOption = options.sortBy || sortBy;
      const filterOptions = options.filters || filters;

      // Fetch stats and recent reviews in parallel
      const [statsData, reviewsData] = await Promise.all([
        getProductReviewStats(productId),
        getProductReviews(productId, { 
          limit: 10, 
          sortBy: sortOption,
          ...filterOptions
        })
      ]);

      setStats(statsData);
      setReviews(reviewsData.reviews || []);
    } catch (err) {
      console.error('Error fetching review data:', err);
      setError('Unable to load reviews. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchReviewData();
    }
  }, [productId]);

  // Show/hide sticky button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (!formRef.current || !sectionRef.current) return;
      
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const formRect = formRef.current.getBoundingClientRect();
      
      // Show sticky button when:
      // 1. Section is in view (top is above viewport bottom)
      // 2. Form is below viewport (not visible)
      // 3. We have reviews (form is collapsed by default)
      const sectionInView = sectionRect.top < window.innerHeight && sectionRect.bottom > 0;
      const formBelowViewport = formRect.top > window.innerHeight;
      
      setShowStickyButton(sectionInView && formBelowViewport && hasReviews);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [reviews]);

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    fetchReviewData({ sortBy: newSort, filters });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchReviewData({ sortBy, filters: newFilters });
  };

  const handleReviewSubmitted = () => {
    fetchReviewData();
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Check if we have reviews
  const hasReviews = stats && stats.totalReviews > 0;

  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-[#fdf9f7] to-[#f8f3f0] border border-[#e8ddd6] p-10">
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#ab3d22]/[0.05] rounded-full blur-3xl animate-pulse"></div>
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 border-3 border-[#ab3d22]/20 border-t-[#ab3d22] rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-12 h-12 border-3 border-transparent border-b-[#ab3d22]/30 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
          </div>
          <span className="text-gray-500 text-sm font-medium">Loading reviews...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-700 text-sm">{error}</p>
        <button 
          onClick={() => fetchReviewData()}
          className="mt-2 text-red-600 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div ref={sectionRef} className="relative">
      <div className="space-y-5">
        {/* 1. Community Rating Summary - First (COSME-style) */}
        <ReviewStats stats={stats} />

        {/* 2. Recent Reviews - Show social proof before form (only if reviews exist) */}
        {hasReviews && (
          <ReviewList 
            reviews={reviews} 
            limit={5}
            onSortChange={handleSortChange}
            onFilterChange={handleFilterChange}
            currentSort={sortBy}
            currentFilters={filters}
          />
        )}

        {/* Decorative divider between reviews and form */}
        {hasReviews && (
          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-gradient-to-r from-transparent via-[#ab3d22]/20 to-transparent"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[#f5f0eb] px-3 text-xs text-gray-400 font-medium">Share your experience</span>
            </div>
          </div>
        )}

        {/* 3. Review Form - Expanded by default ONLY if no reviews */}
        <div ref={formRef}>
          <ReviewForm
            productId={productId}
            productCategory={productCategory}
            onSubmitSuccess={handleReviewSubmitted}
            expandByDefault={!hasReviews}
          />
        </div>
      </div>

      {/* Sticky "Write a Review" button - appears when scrolling past reviews */}
      {showStickyButton && (
        <button
          onClick={scrollToForm}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#ab3d22] text-white px-4 py-2.5 rounded-full shadow-lg hover:bg-[#8a2f1a] transition-all hover:shadow-xl animate-fade-in"
          style={{
            animation: 'fadeInUp 0.3s ease-out'
          }}
        >
          <Edit3 className="h-4 w-4" />
          <span className="text-sm font-medium">Write a Review</span>
        </button>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CommunityReviewSection;
